import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, CheckCircle2, RefreshCw, WandSparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  StepActions,
  StepActionButton,
  RecordConsents,
  DiscreteActionsDropdown,
} from '@/components';
import ConsentStatus from './ConsentStatus';
import { useToast } from '@/components/hooks/use-toast';
import {
  fetchApplicationConsentStatusByClientPolicyApplicationIdRequest,
  postConsentRecordRequest,
  selectApplicationConsentStatusData,
  selectApplicationConsentStatusIsLoading,
  selectPostConsentRecordData,
  selectPostConsentRecordErrorMessage,
  selectSendConsentNotificationEmailData,
  selectSendConsentNotificationEmailError,
  clearSendConsentNotificationEmail,
  clearPostConsentRecord,
  putClientPolicyApplicationStepRequest,
  clearUnderwritingError,
} from '@client/model';
import {
  selectSession,
  selectClient,
  selectPolicyApplicationsError,
  selectPolicyApplicationsStatus,
} from '@client/model/selectors';
import { useClientPolicyApplication } from '@/hooks/useClientPolicyApplication';
import { FundingPolicyType, SessionType } from '@shared';
import { getUserIpAddress } from '@/util/getUserIpAddress';
import { useClientConsentCompletion } from '@/hooks/useClientConsentCompletion';
import { DownloadCarePacketButton } from './DownloadCarePacketButton';
import { ClientPreApplicationConsentFlow } from './ClientPreApplicationConsentFlow';

interface ConsentFormStepProps {
  clientPolicyApplicationId: string;
  consentType: 'submit' | 'start';
  stepType: 'preapplication-consent-form' | 'consent-form';
  stepStatus?: PolicyApplicationStep['status'];
  onClientCompletionChange?: (isComplete: boolean) => void;
}

export function ConsentFormStep({
  clientPolicyApplicationId,
  consentType,
  stepType,
  stepStatus,
  onClientCompletionChange,
}: ConsentFormStepProps) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { policyApplication } = useClientPolicyApplication();

  const session = useSelector(selectSession);
  const client = useSelector(selectClient);
  const consentStatusData = useSelector(selectApplicationConsentStatusData);
  const isLoadingStatus = useSelector(selectApplicationConsentStatusIsLoading);
  const postConsentRecordData = useSelector(selectPostConsentRecordData);
  const postConsentRecordError = useSelector(selectPostConsentRecordErrorMessage);
  const emailNotificationData = useSelector(selectSendConsentNotificationEmailData);
  const emailNotificationError = useSelector(selectSendConsentNotificationEmailError);
  const policyApplicationError = useSelector(selectPolicyApplicationsError);
  const policyApplicationStatus = useSelector(selectPolicyApplicationsStatus);

  const [showDownloadCarePacketButton, setShowDownloadCarePacketButton] = useState(
    consentType === 'start',
  );

  const [isCarePacketDownloaded, setIsCarePacketDownloaded] = useState(false);
  const [celebrateCompletion, setCelebrateCompletion] = useState(false);

  // State to track batch consent submission
  const [batchSubmission, setBatchSubmission] = useState({
    isActive: false,
    total: 0,
    successful: 0,
    failed: 0,
  });

  const handleDownloadCarePacket = useCallback(() => {
    setIsCarePacketDownloaded(true);
    setShowDownloadCarePacketButton(false);
    setCelebrateCompletion(true);
  }, []);

  const fetchConsentStatus = useCallback(() => {
    dispatch(
      fetchApplicationConsentStatusByClientPolicyApplicationIdRequest({
        clientPolicyApplicationId,
        queryParams: { consentType },
      }),
    );
  }, [dispatch, clientPolicyApplicationId, consentType]);

  useEffect(() => {
    fetchConsentStatus();
  }, [fetchConsentStatus]);

  // Sync state with consentStatusData changes
  useEffect(() => {
    if (consentStatusData) {
      setShowDownloadCarePacketButton(
        consentType === 'start' && !consentStatusData?.hasRecievedCarePacket,
      );
      setIsCarePacketDownloaded(consentStatusData?.hasRecievedCarePacket || false);
    }
  }, [consentStatusData, policyApplication?.fundingPolicyType, consentType]);

  const currentUser = useMemo(() => {
    if (!session || !consentStatusData) return null;

    const isAdvisor = !!session.advisor;
    const clientId = client.clientId;

    if (isAdvisor) {
      return {
        type: 'advisor' as const,
        isPending: consentStatusData.allConsents.advisor.isPending,
        userId: consentStatusData.allConsents.advisor.userId,
      };
    }

    if (clientId) {
      const isMainClient = consentStatusData.allConsents.client.userId === clientId;
      const isPartnerClient = consentStatusData.allConsents?.partnerClient?.userId === clientId;

      if (isMainClient) {
        return {
          type: 'client' as const,
          isPending: consentStatusData.allConsents.client.isPending,
          userId: clientId,
          hasRecievedCarePacket:
            consentStatusData.allConsents.client?.hasRecievedCarePacket === true,
        };
      }

      if (isPartnerClient) {
        return {
          type: 'partnerClient' as const,
          isPending: consentStatusData.allConsents.partnerClient!.isPending,
          userId: clientId,
        };
      }
    }

    return null;
  }, [session, client, consentStatusData]);

  const isClientConsentFlow =
    session.sessionType === SessionType.client &&
    consentType === 'start' &&
    stepType === 'preapplication-consent-form' &&
    currentUser?.type === 'client';
  const clientPacketDownloaded =
    isCarePacketDownloaded || currentUser?.hasRecievedCarePacket === true;

  useClientConsentCompletion({
    isClientConsentFlow,
    isConsentPending: currentUser?.isPending ?? true,
    isPacketDownloaded: clientPacketDownloaded,
    onCompletionChange: onClientCompletionChange,
  });

  useEffect(() => {
    if (emailNotificationData) {
      toast({
        title: 'Email Sent',
        description: 'Consent reminder email has been sent successfully.',
        className: 'bg-green-50 text-green-800',
      });
      dispatch(clearSendConsentNotificationEmail());
    }
  }, [emailNotificationData, toast, dispatch]);

  useEffect(() => {
    if (emailNotificationError) {
      toast({
        title: 'Failed to Send Email',
        description: 'Could not send the reminder email. Please try again.',
        className: 'bg-red-50 text-red-800',
      });
      dispatch(clearSendConsentNotificationEmail());
    }
  }, [emailNotificationError, toast, dispatch]);

  // Handle putClientPolicyApplicationStep errors
  useEffect(() => {
    if (policyApplicationStatus === 'savingStepStatusError' && policyApplicationError) {
      toast({
        title: 'Failed to Continue',
        description: policyApplicationError,
        className: 'bg-red-50 text-red-800',
      });
      dispatch(clearUnderwritingError());
    }
  }, [policyApplicationStatus, policyApplicationError, toast, dispatch]);

  useEffect(() => {
    if (postConsentRecordData && batchSubmission.isActive) {
      // Increment successful count
      setBatchSubmission(prev => ({
        ...prev,
        successful: prev.successful + 1,
      }));

      dispatch(clearPostConsentRecord());
    }
  }, [postConsentRecordData, batchSubmission.isActive, dispatch]);

  useEffect(() => {
    if (postConsentRecordError && batchSubmission.isActive) {
      // Increment failed count
      setBatchSubmission(prev => ({
        ...prev,
        failed: prev.failed + 1,
      }));

      dispatch(clearPostConsentRecord());
    }
  }, [postConsentRecordError, batchSubmission.isActive, dispatch]);

  // Handle batch completion
  useEffect(() => {
    if (
      batchSubmission.isActive &&
      batchSubmission.successful + batchSubmission.failed === batchSubmission.total
    ) {
      if (batchSubmission.failed === 0) {
        toast({
          title: 'Consents Recorded',
          description: 'Your consents have been successfully recorded.',
          className: 'bg-green-50 text-green-800',
        });
        fetchConsentStatus();
      } else {
        toast({
          title: 'Error Recording Consents',
          description: 'Failed to record your consents. Please try again.',
          className: 'bg-red-50 text-red-800',
        });
      }
      setBatchSubmission({
        isActive: false,
        total: 0,
        successful: 0,
        failed: 0,
      });
    }
  }, [batchSubmission, toast, fetchConsentStatus]);

  const handleSubmitConsent = useCallback(
    async (consentIds: string[]) => {
      // Initialize batch tracking
      setBatchSubmission({
        isActive: true,
        total: consentIds.length,
        successful: 0,
        failed: 0,
      });

      const ipAddress = await getUserIpAddress();

      consentIds.forEach(consentId =>
        dispatch(
          postConsentRecordRequest({
            clientId: client.clientId,
            clientPolicyApplicationId,
            resourceType: 'ClientPolicyApplicationConsents',
            consentId,
            consentType,
            ipAddress,
            userAgent: navigator.userAgent,
            metadata: {
              stepType: 'consent-form',
              clientPolicyApplicationId,
            },
          }),
        ),
      );
    },
    [client, dispatch, clientPolicyApplicationId, consentType],
  );

  const handleSkipStep = useCallback(() => {
    dispatch(
      putClientPolicyApplicationStepRequest({
        clientPolicyApplicationId,
        stepType,
        status: 'skipped',
      }),
    );
  }, [dispatch, clientPolicyApplicationId, stepType]);

  if (isLoadingStatus) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="text-primary mx-auto mb-4 h-8 w-8 animate-spin" />
          <p className="text-gray-700">Loading consent status...</p>
        </div>
      </div>
    );
  }

  if (isClientConsentFlow && currentUser?.type === 'client') {
    const carePacketFiles = consentStatusData?.carePacketFileUrls || [];

    return (
      <ClientPreApplicationConsentFlow
        clientName={`${client.clientFirstName} ${client.clientLastName}`}
        isCareScout={
          policyApplication?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance
        }
        isConsentPending={currentUser.isPending}
        isSubmitting={batchSubmission.isActive}
        isPacketDownloaded={clientPacketDownloaded}
        celebrateCompletion={celebrateCompletion}
        onSubmit={handleSubmitConsent}
        downloadAction={
          <DownloadCarePacketButton
            applicationId={clientPolicyApplicationId}
            clientId={client.clientId}
            filesToDownload={carePacketFiles}
            onDownloadComplete={handleDownloadCarePacket}
            buttonProps={{
              variant: 'default',
              size: 'lg',
              className: 'h-14 w-full text-base',
            }}
          />
        }
        downloadAgainAction={
          carePacketFiles.length > 0 ? (
            <DownloadCarePacketButton
              applicationId={clientPolicyApplicationId}
              clientId={client.clientId}
              filesToDownload={carePacketFiles}
              text="Download again"
              onDownloadComplete={() => {}}
              buttonProps={{
                variant: 'link',
                className: 'text-primary h-auto p-0 underline underline-offset-4',
              }}
            />
          ) : null
        }
      />
    );
  }

  return (
    <div className="relative space-y-6 p-2">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">Waterlily Consent Agreements</h3>
          <p className="text-sm text-gray-700">
            Please review and agree to the following consent statements to proceed with your
            application.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchConsentStatus}
          disabled={isLoadingStatus}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isLoadingStatus ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {currentUser && currentUser.isPending ? (
        <RecordConsents
          userType={currentUser.type}
          onSubmit={consentIds => handleSubmitConsent(consentIds)}
          submitButtonProps={{ disabled: batchSubmission.isActive }}
          consentType={consentType}
          showPdf={
            consentType === 'start' &&
            currentUser.type !== 'advisor' && // only show pdf for clients & partner
            policyApplication?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance
          }
          isCareScout={
            policyApplication?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance
          }
        />
      ) : (
        <ConsentStatus
          consentStatusData={consentStatusData}
          currentUser={currentUser}
          clientPolicyApplicationId={clientPolicyApplicationId}
          consentType={consentType}
        />
      )}

      <StepActions>
        {session.sessionType === SessionType.wizard && stepStatus !== 'skipped' && (
          <DiscreteActionsDropdown
            actions={[
              {
                id: 'skip',
                label: 'Skip Step',
                icon: <WandSparkles className="h-4 w-4" />,
                onClick: handleSkipStep,
                disabled: policyApplicationStatus === 'savingStepStatus',
                isLoading: policyApplicationStatus === 'savingStepStatus',
                loadingText: 'Processing...',
              },
            ]}
          />
        )}
        {showDownloadCarePacketButton &&
          currentUser &&
          currentUser?.type !== 'advisor' &&
          !currentUser?.hasRecievedCarePacket && (
            <DownloadCarePacketButton
              applicationId={clientPolicyApplicationId}
              clientId={client.clientId}
              filesToDownload={consentStatusData?.carePacketFileUrls || []}
              onDownloadComplete={handleDownloadCarePacket}
            />
          )}
        {consentStatusData?.applicationReady &&
          ((currentUser && currentUser?.type == 'advisor') ||
            (!showDownloadCarePacketButton && isCarePacketDownloaded)) && (
            <StepActionButton
              onClick={() => {
                dispatch(
                  putClientPolicyApplicationStepRequest({
                    clientPolicyApplicationId,
                    stepType,
                    status: 'completed',
                  }),
                );
              }}
              icon={<CheckCircle2 className="h-5 w-5" />}
            >
              Continue to Next Step
            </StepActionButton>
          )}
      </StepActions>
    </div>
  );
}
